import { computed, watch } from 'vue'

import { useLocalStorage } from '@vueuse/core'

import { isEmbedRuntime, isFramed } from '@/utils/embed'

export type ColorScheme = 'light' | 'dark'

const STORAGE_KEY = 'evergpt:color-scheme'

/**
 * The embeddable public-GPT widget renders inside customer pages, so it stays light
 * regardless of the preference a user picked in the full app (same origin, same
 * localStorage).
 */
const isThemeLocked = () => isEmbedRuntime() || isFramed()

const storedScheme = useLocalStorage<ColorScheme>(STORAGE_KEY, 'light')

const colorScheme = computed<ColorScheme>(() =>
  !isThemeLocked() && storedScheme.value === 'dark' ? 'dark' : 'light',
)

/**
 * @foundever/ui-lib declares every semantic colour token as `light-dark()`, so the
 * whole design system follows `color-scheme`. It is set on `<html>` (not a wrapper)
 * so teleported overlays — modals, drawers, dropdown menus, date pickers — follow too.
 */
const applyColorScheme = (scheme: ColorScheme) => {
  const root = document.documentElement
  root.style.colorScheme = scheme
  root.dataset.uiTheme = scheme
}

let isInitialised = false

/** Applies the stored preference. Call once before the app mounts to avoid a light flash. */
export const initColorScheme = () => {
  if (isInitialised) return
  isInitialised = true
  watch(colorScheme, applyColorScheme, { immediate: true })
}

export const useColorScheme = () => {
  initColorScheme()

  const isDark = computed(() => colorScheme.value === 'dark')

  const setColorScheme = (scheme: ColorScheme) => {
    storedScheme.value = scheme
  }

  const toggleColorScheme = () => {
    setColorScheme(isDark.value ? 'light' : 'dark')
  }

  return { colorScheme, isDark, setColorScheme, toggleColorScheme }
}
